import { usePage } from '@inertiajs/react';
import {
    ArrowRight,
    Facebook,
    Instagram,
    Mail,
    MapPin,
    Phone,
    Sparkles,
} from 'lucide-react';


const footerLinks = {
    shop: [
        { label: 'All Caps', href: '/all-caps' },
        { label: 'New In', href: '/new-in' },
        { label: 'Flash Deals', href: '/flash-deals' },
        { label: 'Collections', href: '/collections' },
    ],
    help: [
        { label: 'Track Order', href: '/track-order' },
        { label: 'Return Policy', href: '/return-policy' },
        { label: 'Contact', href: '/contact' },
        { label: 'Checkout', href: '/checkout' },
    ],
};

export function Footer() {
    const { settings = {} } = usePage().props;
    const appName = settings.name || 'Captex';
    const appLogo = settings.footerLogo || settings.logo || null;
    const contactEmail = settings.email || settings.support_email || 'support@captex.local';
    const contactPhone = settings.phone || settings.mobile || settings.contact_number || '+880';
    const contactAddress = settings.address || settings.location || 'Bangladesh';

    return (
        <footer className="relative mt-14 overflow-hidden bg-[#0f0b0d] text-white">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(245,27,117,0.22),transparent_26%),radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.09),transparent_30%),linear-gradient(180deg,rgba(255,255,255,0.04)_0%,rgba(255,255,255,0)_20%)]" />

            <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 relative py-10 sm:py-14 lg:py-16">
                <div className="grid gap-10 lg:grid-cols-[1.08fr_0.92fr]">
                    <div className="space-y-8">
                        <div className="inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/6 px-4 py-2 text-[10px] font-black uppercase tracking-[0.3em] text-white/65">
                            <Sparkles className="size-4" />
                            Built for fresh drops and smooth checkout
                        </div>

                        <div className="flex flex-wrap items-center gap-4">
                            {appLogo ? (
                                <img
                                    src={appLogo}
                                    alt={appName}
                                    className="size-16 rounded-[1.35rem] border border-white/10 bg-white object-contain p-2 shadow-[0_20px_45px_rgba(0,0,0,0.35)]"
                                />
                            ) : (
                                <div className="flex size-16 items-center justify-center rounded-[1.35rem] border border-white/10 bg-white text-base font-black text-black shadow-[0_20px_45px_rgba(0,0,0,0.35)]">
                                    CT
                                </div>
                            )}

                            <div>
                                <p className="text-[11px] font-black uppercase tracking-[0.35em] text-white/40">
                                    Captex Studio
                                </p>
                                <h3 className="mt-2 text-4xl font-black uppercase tracking-[-0.05em] sm:text-5xl">
                                    {appName}
                                </h3>
                            </div>
                        </div>

                        <p className="max-w-2xl text-sm leading-7 text-white/68 sm:text-base">
                            Fashion-forward collections, gift-friendly drops, and a
                            checkout flow that stays fast and focused. Theme 2 is
                            designed to feel softer, warmer, and more editorial.
                        </p>

                        <div className="grid gap-3 sm:grid-cols-3">
                            <ContactCard label="Contact" value={contactEmail} href={`mailto:${contactEmail}`} />
                            <ContactCard label="Call" value={contactPhone} href={`tel:${contactPhone}`} />
                            <ContactCard label="Location" value={contactAddress} icon={MapPin} />
                        </div>

                        <div className="flex flex-wrap gap-3">
                            <a
                                href="/shop"
                                className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-black uppercase tracking-[0.14em] text-black transition hover:-translate-y-0.5 hover:bg-neutral-200"
                            >
                                Shop Now
                                <ArrowRight className="size-4" />
                            </a>
                            <a
                                href="/track-order"
                                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 text-sm font-black uppercase tracking-[0.14em] text-white transition hover:-translate-y-0.5 hover:border-white/35 hover:bg-white/5"
                            >
                                Track Order
                            </a>
                        </div>

                        <div className="flex flex-wrap items-center gap-3 text-sm text-white/58">
                            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2">
                                <Sparkles className="size-4" />
                                Fresh drops weekly
                            </span>
                            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2">
                                <Phone className="size-4" />
                                Fast support
                            </span>
                            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2">
                                <MapPin className="size-4" />
                                Bangladesh
                            </span>
                        </div>
                    </div>

                    <div className="space-y-6">
                        <div className="grid gap-6 sm:grid-cols-2">
                            <NavCard title="Shop" links={footerLinks.shop} />
                            <NavCard title="Help" links={footerLinks.help} />
                        </div>

                        <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-white/6 p-6 shadow-[0_24px_70px_rgba(0,0,0,0.28)] backdrop-blur-sm">
                            <div className="absolute right-0 top-0 h-24 w-24 translate-x-1/3 -translate-y-1/3 rounded-full bg-[#f51b75]/20 blur-2xl" />

                            <div className="relative flex flex-col gap-5">
                                <div>
                                    <p className="text-[10px] font-black uppercase tracking-[0.32em] text-white/40">
                                        Newsletter
                                    </p>
                                    <h4 className="mt-2 text-2xl font-black uppercase tracking-[-0.03em]">
                                        Get early access to new drops
                                    </h4>
                                    <p className="mt-3 text-sm leading-6 text-white/65">
                                        Be first to know when a new collection lands,
                                        a sale starts, or a limited restock goes live.
                                    </p>
                                </div>

                                <div className="flex flex-wrap gap-3">
                                    <a
                                        href={`mailto:${contactEmail}`}
                                        className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-black uppercase tracking-[0.14em] text-black transition hover:-translate-y-0.5 hover:bg-neutral-200"
                                    >
                                        <Mail className="size-4" />
                                        Email Us
                                    </a>
                                    <a
                                        href="https://instagram.com"
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 text-sm font-black uppercase tracking-[0.14em] text-white transition hover:-translate-y-0.5 hover:border-white/35 hover:bg-white/5"
                                    >
                                        <Instagram className="size-4" />
                                        Instagram
                                    </a>
                                    <a
                                        href="https://facebook.com"
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 text-sm font-black uppercase tracking-[0.14em] text-white transition hover:-translate-y-0.5 hover:border-white/35 hover:bg-white/5"
                                    >
                                        <Facebook className="size-4" />
                                        Facebook
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-10 flex flex-col gap-3 border-t border-white/10 pt-6 text-xs text-white/42 sm:flex-row sm:items-center sm:justify-between">
                    <p>(c) 2026 {appName}. All rights reserved.</p>
                    <p className="uppercase tracking-[0.2em]">
                        Crafted for smooth shopping and quick checkout
                    </p>
                </div>
            </div>
        </footer>
    );
}

function NavCard({ title, links }) {
    return (
        <div className="rounded-[1.75rem] border border-white/10 bg-white/6 p-6 backdrop-blur-sm">
            <h4 className="text-xs font-black uppercase tracking-[0.25em] text-white/45">
                {title}
            </h4>
            <div className="mt-5 space-y-3 text-sm text-white/72">
                {links.map((link) => (
                    <a
                        key={link.label}
                        href={link.href}
                        className="flex items-center justify-between gap-3 transition hover:text-white"
                    >
                        <span>{link.label}</span>
                        <ArrowRight className="size-4 opacity-40" />
                    </a>
                ))}
            </div>
        </div>
    );
}

function ContactCard({ label, value, href, icon: Icon }) {
    const content = (
        <div className="rounded-2xl border border-white/10 bg-white/6 p-4 backdrop-blur-sm">
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-white/40">
                {label}
            </p>
            <div className="mt-2 flex items-start gap-2">
                {Icon ? <Icon className="mt-0.5 size-4 shrink-0 text-white/50" /> : null}
                <span className="text-sm font-semibold text-white/85">{value}</span>
            </div>
        </div>
    );

    if (!href) return content;

    return (
        <a href={href} className="block transition hover:-translate-y-0.5">
            {content}
        </a>
    );
}
